/**
 * ModelPicker — searchable dropdown of OpenRouter models.
 *
 * Fetches the model list once (cached server-side for 10 min) and lets the
 * operator search by id or name with keyboard-only navigation via the
 * shadcn Command component. Free-text fallback: pasting a model slug not
 * in the list still works because we save whatever's in the input.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export default function ModelPicker({
  value,
  onChange,
  placeholder = "Pick an OpenRouter model…",
  testId = "openrouter-model-picker",
}) {
  const [open, setOpen] = useState(false);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  async function fetchModels() {
    if (loaded) return;
    setLoading(true);
    try {
      const r = await api.get("/settings/openrouter/models");
      setModels(r.data?.models || []);
      setLoaded(true);
    } catch {
      /* silent — the input still accepts free-text pastes */
    } finally { setLoading(false); }
  }

  // Lazy fetch on first open so the settings page load stays fast.
  useEffect(() => {
    if (open) fetchModels();
  }, [open, loaded]);

  const selected = models.find((m) => m.id === value);
  const label = selected
    ? (selected.name || selected.id)
    : (value || placeholder);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between border-zinc-300 mt-1.5 font-mono-num text-xs h-9"
          data-testid={testId}
        >
          <span className={cn("truncate", !value && "text-zinc-400")}>{label}</span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={true}>
          <CommandInput
            placeholder="Search models (e.g. kimi, sonnet, gpt)…"
            className="h-9"
            data-testid={`${testId}-search`}
          />
          <CommandList className="max-h-72">
            {loading && (
              <div className="p-3 text-xs text-zinc-500 flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading models…
              </div>
            )}
            {!loading && (
              <CommandEmpty>
                No match. Paste any model slug to use it anyway.
              </CommandEmpty>
            )}
            {!loading && models.length > 0 && (
              <CommandGroup>
                {models.map((m) => (
                  <CommandItem
                    key={m.id}
                    value={`${m.id} ${m.name || ""}`}
                    onSelect={() => {
                      onChange(m.id);
                      setOpen(false);
                    }}
                    className="cursor-pointer"
                    data-testid={`${testId}-item-${m.id}`}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-3.5 w-3.5 shrink-0",
                        value === m.id ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="font-mono-num text-xs truncate">{m.id}</span>
                        <span className="ml-auto shrink-0 font-mono-num text-2xs text-zinc-500 tabular-nums">
                          {formatPricing(m.pricing_prompt, m.pricing_completion)}
                        </span>
                      </div>
                      {m.name && (
                        <div className="text-2xs text-zinc-500 truncate">
                          {m.name}
                          {m.context_length ? ` · ${m.context_length.toLocaleString()} ctx` : ""}
                        </div>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}


/**
 * OpenRouter pricing is `$/token` as a string. Convert to $/M tokens and
 * render as ``$P / $C /M`` (P = prompt, C = completion). Returns an empty
 * string when both are zero or unknown so free / unknown-cost models don't
 * get a misleading ``$0.00`` label.
 */
function formatPricing(prompt, completion) {
  const p = toMillion(prompt);
  const c = toMillion(completion);
  if (p == null && c == null) return "";
  if (p === 0 && c === 0) return "free";
  const pStr = p == null ? "?" : `$${fmt(p)}`;
  const cStr = c == null ? "?" : `$${fmt(c)}`;
  return `${pStr} / ${cStr} /M`;
}

function toMillion(v) {
  if (v == null) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return n * 1_000_000;
}

function fmt(n) {
  // Drop trailing zeros — $3.00 → $3, $1.20 → $1.2, $0.05 → $0.05
  if (n >= 100) return n.toFixed(0);
  if (n >= 1) return String(Number(n.toFixed(2)));
  return String(Number(n.toFixed(3)));
}
